import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toastr } from 'react-redux-toastr';
import { Icon } from 'semantic-ui-react';
import moment from 'moment';

import FlexContainer from 'common/components/atoms/FlexContainer';
import Button from 'common/components/atoms/Button';
import InputField from 'common/components/Chart/atoms/InputField';
import LoadingSvg from 'common/images/LoadingSvg';
import { isPetasenseAdmin } from 'common/rbac/util';
import AccessRestricted from 'common/components/AccessRestricted/AccessRestricted';
import { countryCodes } from 'common/CountryPhoneCodes';
import { axiosInstance, ENDPOINT } from 'common/constants';
import { handleResponse, localTimeString } from 'common/helpers';
import {
  getPartnerPrimaryColor,
  getPartnerSearchInputBorder
} from 'common/styles/organisationPartnerTheme';

const Page = styled.div`
  padding: 16px 24px;
  height: calc(100vh - var(--title-bar-offset, 0px) - 130px);
  overflow: auto;
  background-color: ${props => props.theme.colors.white};
`;

const HeaderRow = styled(FlexContainer)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const HeaderSubtitle = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 2px;
`;

const SearchWrapper = styled.div`
  margin-bottom: 16px;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid ${props => getPartnerSearchInputBorder(props.theme)};
  border-radius: 9px;
  min-height: 36px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;

  &:focus {
    outline: none;
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    box-shadow: 0 0 0 1px ${props => getPartnerPrimaryColor(props.theme)};
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #888;
  font-style: italic;
  margin-top: 8px;
`;

const LoadingWrapper = styled(FlexContainer)`
  height: calc(100vh - var(--title-bar-offset, 0px) - 280px);
  align-items: center;
  justify-content: center;
`;

const UserCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 10px;
  overflow: hidden;
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background: #fafafa;
  border-bottom: ${props => (props.expanded ? '1px solid #eee' : 'none')};

  &:hover {
    background: ${props => getPartnerPrimaryColor(props.theme)}12;
  }
`;

const UserHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 1px;
`;

const UserMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 10px;
  ${(props) => {
    if (props.status === 'active') return 'background: #e8f5e9; color: #388e3c;';
    if (props.status === 'deactivated') return 'background: #fdecea; color: #c62828;';
    return 'background: #f5f5f5; color: #888;';
  }}
`;

const SiteList = styled.div`
  font-size: 11px;
  color: #888;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Gives the Inactive badge its meaning, same as the Last active column in Settings
const LastActive = styled.div`
  font-size: 11px;
  color: #888;
  white-space: nowrap;
`;

const UserBody = styled.div`
  padding: 16px 20px 20px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6b6b6b;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin-bottom: 14px;
`;

const FieldRow = styled(FlexContainer)`
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 240px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b6b6b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const ReadonlyValue = styled.div`
  font-size: 14px;
  color: #333;
  padding: 7px 0;
`;

const PhoneRow = styled(FlexContainer)`
  gap: 8px;
  align-items: flex-start;
`;

const StyledInputField = styled(InputField)`
  width: 100%;
  min-width: 0;
`;

const PhoneCodeSelect = styled.select`
  min-width: 90px;
  width: 90px;
  height: 36px;
  border: 1px solid #d4d4d4;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  color: #333;
  background: #fff;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
  }
`;

const ActionRow = styled(FlexContainer)`
  gap: 10px;
  align-items: center;
  margin-top: 16px;
`;

const SaveBtn = styled(Button)`
  &&& {
    min-height: 32px;
    padding: 0 18px;
  }
`;

const CancelBtn = styled(Button)`
  &&& {
    background: #fff;
    color: #111;
    border: 1px solid #d4d4d4;
    min-height: 32px;
    padding: 0 18px;
  }
  &&&:hover {
    background-color: ${props => getPartnerPrimaryColor(props.theme)};
    border-color: ${props => getPartnerPrimaryColor(props.theme)};
    color: #fff;
  }
`;

const parsePhone = (phone) => {
  if (!phone) return { phoneCode: 'NA', phoneNumber: '' };
  const parts = phone.split('/');
  if (parts.length === 2) return { phoneCode: parts[0], phoneNumber: parts[1] };
  return { phoneCode: 'NA', phoneNumber: parts[0] || '' };
};

const formatOrganizations = (orgs) => orgs || '—';

// Same three states as the Account Users list — keep in sync with UserItem.js.
// 'inactive' is last-seen recency, not account status, so the header renders the
// last-active timestamp beside the badge the way that list does. Without it the
// badge reads as "this account is disabled".
const getUserStatusKey = (user) => {
  // Explicit false only — a missing `active` should not read as deactivated
  if (user.active === false) return 'deactivated';
  if (user.last_active_at && moment(user.last_active_at) > moment().subtract(7, 'days')) {
    return 'active';
  }
  return 'inactive';
};

class AdminUsers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
      users: [],
      loading: false,
      searched: false,
      expandedUserId: null,
      editState: {},
      saving: false
    };
    this._searchTimer = null;
  }

  componentDidMount() {
    const { t, title } = this.props;
    const brand = title || t('pageTitles.defaultBrand', { defaultValue: 'Petasense' });
    document.title = `${brand} | ${t('adminUsers.page.title', { defaultValue: 'Users' })}`;
  }

  componentWillUnmount() {
    clearTimeout(this._searchTimer);
  }

  handleSearchChange = (e) => {
    const searchQuery = e.target.value;
    this.setState({ searchQuery });
    clearTimeout(this._searchTimer);
    if (searchQuery.length < 2) {
      this.setState({ users: [], searched: false, loading: false });
      return;
    }
    this._searchTimer = setTimeout(() => this.fetchUsers(searchQuery), 400);
  };

  fetchUsers = (query) => {
    this.setState({ loading: true, searched: false });
    handleResponse(axiosInstance.get(ENDPOINT.GET_USERS(query)))
      .then((res) => {
        const users = (res && res.result) || (res && res.items) || (Array.isArray(res) ? res : []);
        this.setState({ users, loading: false, searched: true });
      })
      .catch(() => {
        this.setState({ users: [], loading: false, searched: true });
      });
  };

  toggleUser = (user) => {
    const { expandedUserId } = this.state;
    if (expandedUserId === user.id) {
      this.setState({ expandedUserId: null, editState: {} });
      return;
    }
    const { phoneCode, phoneNumber } = parsePhone(user.phone);
    this.setState({
      expandedUserId: user.id,
      editState: {
        email: user.email || '',
        phoneCode,
        phoneNumber
      }
    });
  };

  handleEditChange = (field, value) => {
    this.setState(prev => ({
      editState: { ...prev.editState, [field]: value }
    }));
  };

  handlePhoneCodeChange = (e) => {
    this.handleEditChange('phoneCode', e.target.value);
  };

  // Compared against the original values, so undoing an edit re-disables Save
  isDirty = (user) => {
    const { editState } = this.state;
    const { phoneCode, phoneNumber } = parsePhone(user.phone);
    return (editState.email || '') !== (user.email || '')
      || (editState.phoneCode || 'NA') !== phoneCode
      || (editState.phoneNumber || '') !== phoneNumber;
  };

  saveUser = (user) => {
    const { t } = this.props;
    const { editState } = this.state;
    const { email, phoneCode, phoneNumber } = editState;

    const payload = {};
    if (email !== user.email) payload.email = email;

    if (phoneCode && phoneCode !== 'NA' && !phoneNumber) {
      toastr.error(t('adminUsers.toast.phoneNumberRequired', { defaultValue: 'Please enter a phone number with the country code.' }));
      return;
    }
    // A number without a country code is not dialable by Twilio (SMS, 2FA OTP)
    if (phoneNumber && (!phoneCode || phoneCode === 'NA')) {
      toastr.error(t('adminUsers.toast.countryCodeRequired', { defaultValue: 'Please select a country code for the phone number.' }));
      return;
    }
    const newPhone = phoneNumber ? `${phoneCode}/${phoneNumber}` : null;
    const existingPhone = user.phone || null;
    if (newPhone !== existingPhone) payload.phone = newPhone;

    if (Object.keys(payload).length === 0) {
      toastr.info(t('adminUsers.toast.noChanges', { defaultValue: 'No changes to save.' }));
      return;
    }

    this.setState({ saving: true });
    handleResponse(axiosInstance.put(ENDPOINT.UPDATE_USER(user.id), payload))
      .then((res) => {
        toastr.success(res && res.message
          ? res.message
          : t('adminUsers.toast.saveSuccess', { defaultValue: 'User updated successfully.' }));
        this.setState(prev => ({
          saving: false,
          users: prev.users.map(u => (u.id === user.id ? { ...u, ...payload } : u)),
          expandedUserId: null,
          editState: {}
        }));
      })
      .catch((err) => {
        toastr.error(
          (err && err.message)
          || t('adminUsers.toast.saveError', { defaultValue: 'Failed to update user.' })
        );
        this.setState({ saving: false });
      });
  };

  renderUserCard = (user) => {
    const { t } = this.props;
    const { expandedUserId, editState, saving } = this.state;
    const expanded = expandedUserId === user.id;
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
    const siteNames = formatOrganizations(user.organizations);
    const statusKey = getUserStatusKey(user);

    return (
      <UserCard key={user.id}>
        <UserHeader expanded={expanded} onClick={() => this.toggleUser(user)}>
          <UserHeaderLeft>
            <Icon name={expanded ? 'chevron down' : 'chevron right'} />
            <div style={{ minWidth: 0 }}>
              <UserName>{fullName}</UserName>
              <UserEmail>{user.email}</UserEmail>
            </div>
          </UserHeaderLeft>
          <UserMeta>
            <SiteList title={siteNames}>{siteNames}</SiteList>
            <LastActive>
              {user.last_active_at
                ? t('adminUsers.lastActive.seen', {
                  time: localTimeString(user.last_active_at),
                  defaultValue: 'Last active {{time}}'
                })
                : t('adminUsers.lastActive.never', { defaultValue: 'Never logged in' })}
            </LastActive>
            <StatusBadge status={statusKey}>
              {t(`settings.accountUsers.status.${statusKey}`)}
            </StatusBadge>
          </UserMeta>
        </UserHeader>

        {expanded && (
          <UserBody>
            <SectionLabel>
              {t('adminUsers.edit.sectionLabel', { defaultValue: 'Edit contact details' })}
            </SectionLabel>
            <FieldRow>
              <FieldGroup>
                <FieldLabel>{t('adminUsers.fields.name', { defaultValue: 'Name' })}</FieldLabel>
                <ReadonlyValue>{fullName}</ReadonlyValue>
              </FieldGroup>
              <FieldGroup style={{ minWidth: 280 }}>
                <StyledInputField
                  label={t('adminUsers.fields.email', { defaultValue: 'Email address' })}
                  type="text"
                  name="email"
                  value={editState.email || ''}
                  onChange={e => this.handleEditChange('email', e.target.value)}
                  placeholder="user@example.com"
                />
              </FieldGroup>
            </FieldRow>
            <FieldGroup style={{ marginTop: 8 }}>
              <FieldLabel>{t('adminUsers.fields.phone', { defaultValue: 'Phone number' })}</FieldLabel>
              <PhoneRow>
                <PhoneCodeSelect
                  value={editState.phoneCode || 'NA'}
                  onChange={this.handlePhoneCodeChange}
                >
                  <option value="NA">—</option>
                  {countryCodes.map(c => (
                    <option key={c.value} value={c.value}>{c.text}</option>
                  ))}
                </PhoneCodeSelect>
                <StyledInputField
                  type="text"
                  name="phoneNumber"
                  value={editState.phoneNumber || ''}
                  onChange={e => this.handleEditChange('phoneNumber', e.target.value)}
                  placeholder="—"
                />
              </PhoneRow>
            </FieldGroup>
            <ActionRow>
              <SaveBtn
                onClick={() => this.saveUser(user)}
                disabled={saving || !this.isDirty(user)}
              >
                {saving
                  ? t('adminUsers.edit.saving', { defaultValue: 'SAVING…' })
                  : t('adminUsers.edit.save', { defaultValue: 'SAVE' })}
              </SaveBtn>
              <CancelBtn
                onClick={() => this.setState({ expandedUserId: null, editState: {} })}
                disabled={saving}
              >
                {t('adminUsers.edit.cancel', { defaultValue: 'CANCEL' })}
              </CancelBtn>
            </ActionRow>
          </UserBody>
        )}
      </UserCard>
    );
  };

  render() {
    const { user, t } = this.props;
    if (!isPetasenseAdmin(user.user)) {
      return <AccessRestricted />;
    }

    const { searchQuery, users, loading, searched } = this.state;

    return (
      <Page>
        <HeaderRow>
          <div>
            <HeaderTitle>{t('adminUsers.page.title', { defaultValue: 'Users' })}</HeaderTitle>
            <HeaderSubtitle>
              {t('adminUsers.page.subtitle', {
                defaultValue: 'Search and edit user contact details across all sites.'
              })}
            </HeaderSubtitle>
          </div>
        </HeaderRow>

        <SearchWrapper>
          <SearchInput
            type="text"
            value={searchQuery}
            autoFocus
            placeholder={t('adminUsers.search.placeholder', {
              defaultValue: 'Search by name or email…'
            })}
            onChange={this.handleSearchChange}
          />
        </SearchWrapper>

        {searchQuery.length > 0 && searchQuery.length < 2 && (
          <Hint>
            {t('adminUsers.search.minChars', { defaultValue: 'Type at least 2 characters to search.' })}
          </Hint>
        )}

        {loading && (
          <LoadingWrapper>
            <LoadingSvg />
          </LoadingWrapper>
        )}

        {!loading && searched && users.length === 0 && (
          <Hint>
            {t('adminUsers.search.noResults', { defaultValue: 'No users found matching your search.' })}
          </Hint>
        )}

        {!loading && users.map(this.renderUserCard)}
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  title: state.companyReducer && state.companyReducer.partner && state.companyReducer.partner.title
});

export default connect(mapStateToProps)(withTranslation()(AdminUsers));
